import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { ArrowLeft, Download, Search, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import { getMonthlyCaseTransferReport } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import "./MonthlyAttornyTransferdetailsReport.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const MAX_BULLETS = 1;

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const formatMoney = (v) =>
  v == null || v === ""
    ? ""
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const joinParts = (...parts) =>
  parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

const splitCodes = (text) =>
  String(text || "")
    .split(";")
    .map((s) => s.replace(/\r?\n/g, " ").trim())
    .filter(Boolean);

const BulletListCell = ({ text }) => {
  const items = splitCodes(text);
  if (!items.length) return <td />;

  const visible = items.slice(0, MAX_BULLETS);
  const hidden = items.slice(MAX_BULLETS);

  return (
    <td>
      <ul className="matd-bullets">
        {visible.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
      {hidden.length > 0 && (
        <span className="matd-more-wrap">
          <span className="matd-more">+{hidden.length} more</span>
          <div className="matd-more-popup">
            <ul className="matd-bullets matd-bullets-all">
              {items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        </span>
      )}
    </td>
  );
};

const MonthlyAttornyTransferdetailsReport = ({
  domain,
  lawFirmId,
  lawFirmName,
  fromDate,
  toDate,
  onBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const loadPage = async (page, size, search = appliedSearch) => {
    if (!domain || !fromDate || !toDate || !lawFirmId) {
      toast.error("Missing filters. Go back and search again.");
      return;
    }
    setLoading(true);
    try {
      const res = await getMonthlyCaseTransferReport({
        domain,
        lawFirmIds: lawFirmId,
        fromDate,
        toDate,
        tabName: "Details",
        searchText: search || "",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.details ? res : res?.data || {};
      setRows(data?.details || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
      setAppliedSearch(search || "");
      if (!(data?.details || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, pageSize, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, lawFirmId, fromDate, toDate]);

  const handleSearch = () => loadPage(1, pageSize, searchText.trim());

  const mapRow = (r) => ({
    "Law Firm Name": r.lawFirmName,
    "Law Firm Id": r.lawFirmId,
    "Case Id": r.caseId,
    "Case No": r.caseNo,
    "Patient Name": r.patientName,
    "Patient Address": joinParts(r.patientAddress, r.patientCity, r.patientState, r.patientZip),
    "Patient Phone": r.patientPhone,
    "Date Of Accident": formatDate(r.dateOfAccident),
    "Date Of Birth": formatDate(r.dateOfBirth),
    "Policy Holder": r.policyHolder,
    "Policy Number": r.policyNumber,
    "Claim Number": r.claimNumber,
    "Insurance Name": r.insuranceName,
    "Insurance Address": joinParts(r.insuranceAddress, r.insuranceCity, r.state, r.insuranceZip),
    "Insurance Phone": r.insurancePhone,
    "Insurance Email": r.insuranceEmail,
    Status: r.statusName,
    "Attorney Name": r.attorneyName,
    "Attorney Address": joinParts(r.attorneyAddress, r.attorneyCity, r.attorneyState, r.attorneyZip),
    "Attorney Fax": r.attorneyFax,
    Provider: r.provider,
    "Bill Number": r.billNumber,
    "Bill Date": formatDate(r.billDate),
    "Start Visit Date": formatDate(r.startVisitDate),
    "End Visit Date": formatDate(r.endVisitDate),
    "Bill Amount": r.billAmount,
    Paid: r.paid,
    Balance: r.balance,
    "Payment Date": formatDate(r.paymentDate),
    "Case Type": r.caseTypeName,
    Location: r.location,
    "Company Id": r.companyId,
    "Diagnosis Codes": r.diagnosisCodes,
    "GYB Procedure Code": r.gybProcedureCode,
    "GYB Procedure Description": r.gybProcedureCodeDescription,
    Specialty: r.specialty,
    "Denial Reason": r.denialReason,
    Verification: r.verification,
    "Is Funded": r.isFunded,
    "Funder Name": r.funderName,
    "Portfolio Name": r.portfolioName,
    "Batch Number": r.batchNumber,
    "Batch From Bill Date": formatDate(r.batchFromBillDate),
    "Batch To Bill Date": formatDate(r.batchToBillDate),
    "Funder Note": r.funderNote,
    "Transferred On": formatDate(r.transferredOn),
  });

  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await getMonthlyCaseTransferReport({
        domain,
        lawFirmIds: lawFirmId,
        fromDate,
        toDate,
        tabName: "Details",
        searchText: appliedSearch || "",
        isExport: true,
      });
      const data = res?.details ? res : res?.data || {};
      const list = data?.details || [];
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      if ((data.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${data.totalCount}`);
      }
      const mapped = list.map(mapRow);
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "MonthlyAttorneyTransfer_Details.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="matd-page">
      <Toaster position="top-right" />

      <div className="matd-header">
        <button type="button" className="matd-btn matd-btn-back" onClick={onBack}>
          <ArrowLeft size={12} />
          Back
        </button>

        <div className="matd-title">
          <h3>
            {lawFirmName || "Law Firm"} ({lawFirmId})
          </h3>
          <span>
            {fromDate} → {toDate}
          </span>
        </div>

        <div className="matd-search-row">
          <input
            className="matd-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Case # / Case Id / Bill # / Patient"
          />
          <button
            type="button"
            className="matd-btn matd-btn-back"
            onClick={handleSearch}
            disabled={loading}
          >
            <Search size={14} />
            Search
          </button>
          <button
            type="button"
            className="matd-btn matd-btn-reset"
            onClick={() => {
              setSearchText("");
              loadPage(1, pageSize, "");
            }}
            disabled={loading}
          >
            <RefreshCw size={14} />
            Reset
          </button>
        </div>

        <button
          type="button"
          className="matd-btn matd-btn-reset"
          onClick={handleDownload}
          disabled={loading || !rows.length}
        >
          <Download size={14} />
          Excel
        </button>
      </div>

      <div className="matd-table-wrap">
        <table className="matd-table">
          <thead>
            <tr>
              <th>Law Firm Name</th>
              <th>Law Firm Id</th>
              <th>Case Id</th>
              <th>Case No</th>
              <th>Patient Name</th>
              <th>Patient Address</th>
              <th>Patient Phone</th>
              <th>Date Of Accident</th>
              <th>Date Of Birth</th>
              <th>Policy Holder</th>
              <th>Policy Number</th>
              <th>Claim Number</th>
              <th>Insurance Name</th>
              <th>Insurance Address</th>
              <th>Insurance Phone</th>
              <th>Insurance Email</th>
              <th>Status</th>
              <th>Attorney Name</th>
              <th>Attorney Address</th>
              <th>Attorney Fax</th>
              <th>Provider</th>
              <th>Bill Number</th>
              <th>Bill Date</th>
              <th>Start Visit Date</th>
              <th>End Visit Date</th>
              <th>Bill Amount</th>
              <th>Paid</th>
              <th>Balance</th>
              <th>Payment Date</th>
              <th>Case Type</th>
              <th>Location</th>
              <th>Company Id</th>
              <th>Diagnosis Codes</th>
              <th>GYB Procedure Code</th>
              <th>GYB Procedure Description</th>
              <th>Specialty</th>
              <th>Denial Reason</th>
              <th>Verification</th>
              <th>Is Funded</th>
              <th>Funder Name</th>
              <th>Portfolio Name</th>
              <th>Batch Number</th>
              <th>Batch From Bill Date</th>
              <th>Batch To Bill Date</th>
              <th>Funder Note</th>
              <th>Transferred On</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={46} className="matd-empty">
                  <div className="matd-empty-inner">
                    {loading ? "Loading..." : "No data"}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.billNumber}|${r.caseId}|${i}`}>
                  <td>{r.lawFirmName}</td>
                  <td>{r.lawFirmId}</td>
                  <td>{r.caseId}</td>
                  <td>{r.caseNo}</td>
                  <td>{r.patientName}</td>
                  <td className="matd-wrap">
                    {joinParts(r.patientAddress, r.patientCity, r.patientState, r.patientZip)}
                  </td>
                  <td>{r.patientPhone}</td>
                  <td>{formatDate(r.dateOfAccident)}</td>
                  <td>{formatDate(r.dateOfBirth)}</td>
                  <td>{r.policyHolder}</td>
                  <td>{r.policyNumber}</td>
                  <td>{r.claimNumber}</td>
                  <td>{r.insuranceName}</td>
                  <td className="matd-wrap">
                    {joinParts(r.insuranceAddress, r.insuranceCity, r.state, r.insuranceZip)}
                  </td>
                  <td>{r.insurancePhone}</td>
                  <td>{r.insuranceEmail}</td>
                  <td>{r.statusName}</td>
                  <td>{r.attorneyName}</td>
                  <td className="matd-wrap">
                    {joinParts(r.attorneyAddress, r.attorneyCity, r.attorneyState, r.attorneyZip)}
                  </td>
                  <td>{r.attorneyFax}</td>
                  <td>{r.provider}</td>
                  <td>{r.billNumber}</td>
                  <td>{formatDate(r.billDate)}</td>
                  <td>{formatDate(r.startVisitDate)}</td>
                  <td>{formatDate(r.endVisitDate)}</td>
                  <td>{formatMoney(r.billAmount)}</td>
                  <td>{formatMoney(r.paid)}</td>
                  <td>{formatMoney(r.balance)}</td>
                  <td>{formatDate(r.paymentDate)}</td>
                  <td>{r.caseTypeName}</td>
                  <td>{r.location}</td>
                  <td>{r.companyId}</td>
                  <BulletListCell text={r.diagnosisCodes} />
                  <td>{r.gybProcedureCode}</td>
                  <td className="matd-wrap">{r.gybProcedureCodeDescription}</td>
                  <td>{r.specialty}</td>
                  <BulletListCell text={r.denialReason} />
                  <BulletListCell text={r.verification} />
                  <td>{r.isFunded}</td>
                  <td>{r.funderName}</td>
                  <td>{r.portfolioName}</td>
                  <td>{r.batchNumber}</td>
                  <td>{formatDate(r.batchFromBillDate)}</td>
                  <td>{formatDate(r.batchToBillDate)}</td>
                  <td className="matd-wrap">{r.funderNote}</td>
                  <td>{formatDate(r.transferredOn)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="matd-pager">
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((o) => o.value === pageSize)}
            onChange={(opt) => loadPage(1, opt.value, appliedSearch)}
            styles={selectStyles}
            menuPlacement="top"
            isSearchable={false}
          />
          <span>
            {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of{" "}
            {totalCount}
          </span>
          <button
            type="button"
            className="matd-btn matd-btn-reset"
            disabled={safePage <= 1 || loading}
            onClick={() => loadPage(safePage - 1, pageSize, appliedSearch)}
          >
            Prev
          </button>
          <button
            type="button"
            className="matd-btn matd-btn-reset"
            disabled={safePage >= totalPages || loading}
            onClick={() => loadPage(safePage + 1, pageSize, appliedSearch)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MonthlyAttornyTransferdetailsReport;